import {FormControl, FormField, TextInput} from 'platform/components';
import {Grid, GridItem, Heading, VStack} from 'platform/foundation';

import {defaultTo} from 'ramda';

import {GetWarehouseHandlingUnitsResponse} from '@dms/api/warehouse';
import i18n from '@dms/i18n';
import {getOptionsFromHandlingUnits} from '@dms/shared-aftersales';

import {Nullish, RequiredTestIdProps, suffixTestId} from 'shared/web';

import {SupplierOrderItemForm} from '../types/SupplierOrderItemForm';

export interface SupplierArticleFormProps extends RequiredTestIdProps {
  control: FormControl<SupplierOrderItemForm>;
  handlingUnit: string | Nullish;
  handlingUnits: GetWarehouseHandlingUnitsResponse | Nullish;
  areHandlingUnitsLoading: boolean;
  supplierName: string | Nullish;
}

export function SupplierArticleForm(props: SupplierArticleFormProps) {
  return (
    <VStack spacing={4} data-testid={props['data-testid']}>
      <Heading size={5}>{i18n.t('entity.warehouse.labels.supplierArticle')}</Heading>

      <Grid columns={4}>
        <GridItem span={1}>
          <FormField
            control={props.control}
            type="number"
            name="supplierArticle.supplierQuantity"
            label={i18n.t('entity.warehouse.labels.supplierQuantity')}
            decimalPlaces={4}
            suffix={props.handlingUnit}
            data-testid={suffixTestId('inputs.supplierQuantity', props)}
          />
        </GridItem>
        <GridItem span={1}>
          <FormField
            control={props.control}
            isDisabled
            isLoading={props.areHandlingUnitsLoading}
            type="choice"
            name="supplierArticle.supplierUnitId"
            label={i18n.t('entity.warehouse.labels.supplierUnit')}
            options={getOptionsFromHandlingUnits(props.handlingUnits)}
            data-testid={suffixTestId('inputs.supplierUnit', props)}
          />
        </GridItem>
        <GridItem span={1}>
          <FormField
            control={props.control}
            type="number"
            name="supplierArticle.warehouseQuantity"
            label={i18n.t('entity.warehouse.labels.warehouseQuantity')}
            decimalPlaces={4}
            suffix={props.handlingUnit}
            data-testid={suffixTestId('inputs.warehouseQuantity', props)}
          />
        </GridItem>
        <GridItem span={1}>
          <FormField
            control={props.control}
            isDisabled
            isLoading={props.areHandlingUnitsLoading}
            type="choice"
            name="supplierArticle.warehouseUnitId"
            label={i18n.t('entity.warehouse.labels.warehouseUnit')}
            options={getOptionsFromHandlingUnits(props.handlingUnits)}
            menuInPortal
            data-testid={suffixTestId('inputs.warehouseUnit', props)}
          />
        </GridItem>
        <GridItem span={1}>
          <FormField
            control={props.control}
            isDisabled
            type="text"
            name="supplierArticle.supplierOrderingNumber"
            label={i18n.t('entity.warehouse.labels.supplierOrderingNumber')}
            data-testid={suffixTestId('inputs.supplierOrderingNumber', props)}
          />
        </GridItem>
        <GridItem span={1}>
          <FormField
            control={props.control}
            type="number"
            name="supplierArticle.supplierBulkPackageQuantity"
            label={i18n.t('entity.warehouse.labels.supplierBulkPackageQuantity')}
            decimalPlaces={4}
            data-testid={suffixTestId('inputs.supplierBulkPackageQuantity', props)}
          />
        </GridItem>
        <GridItem span={1}>
          <TextInput
            isDisabled
            value={defaultTo('', props.supplierName)}
            label={i18n.t('entity.warehouse.labels.supplier')}
            data-testid={suffixTestId('inputs.supplier', props)}
          />
        </GridItem>
        <GridItem span={1}>
          <FormField
            control={props.control}
            isDisabled
            type="text"
            name="supplierArticle.supplierDiscountGroupCode"
            label={i18n.t('entity.warehouse.labels.supplierDiscountGroup')}
            data-testid={suffixTestId('inputs.supplierDiscountGroup', props)}
          />
        </GridItem>
      </Grid>
    </VStack>
  );
}
